<script setup>
import { computed, onMounted, onUnmounted, nextTick, ref, watch } from 'vue';
import { useSidebarContext } from './provider';
import { useRoute, useRouter } from 'vue-router';
import Policy from 'dashboard/components/policy.vue';
import SidebarGroupHeader from './SidebarGroupHeader.vue';
import SidebarGroupLeaf from './SidebarGroupLeaf.vue';
import SidebarSubGroup from './SidebarSubGroup.vue';
import SidebarGroupEmptyLeaf from './SidebarGroupEmptyLeaf.vue';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  name: { type: String, required: true },
  label: { type: String, required: true },
  icon: { type: [String, Object, Function], default: null },
  to: { type: Object, default: null },
  activeOn: { type: Array, default: () => [] },
  children: { type: Array, default: undefined },
  getterKeys: { type: Object, default: () => ({}) },
  onClick: { type: Function, default: null },
  isCollapsed: { type: Boolean, default: false },
  activeMenuName: { type: String, default: null },
});

const emit = defineEmits(['openSubmenu', 'closeSubmenu']);

const {
  expandedItem,
  setExpandedItem,
  resolvePath,
  resolvePermissions,
  resolveFeatureFlag,
  isAllowed,
} = useSidebarContext();

const navigableChildren = computed(() => {
  return props.children?.flatMap(child => child.children || child) || [];
});

const route = useRoute();
const router = useRouter();
const isExpanded = computed(() => expandedItem.value === props.name);
const isExpandable = computed(() => props.children);
const hasChildren = computed(
  () => Array.isArray(props.children) && props.children.length > 0
);

const accessibleItems = computed(() => {
  if (!hasChildren.value) return [];
  return props.children.filter(child => {
    return child.to && isAllowed(child.to);
  });
});

const hasAccessibleSubgroups = computed(() => {
  if (!hasChildren.value) return false;
  return props.children.some(child => {
    if (child.children && child.children.length > 0) {
      return child.children.some(subChild => isAllowed(subChild.to));
    }
    return false;
  });
});

const hasAccessibleChildren = computed(() => {
  return accessibleItems.value.length > 0 || hasAccessibleSubgroups.value;
});

const isActive = computed(() => {
  if (props.to) {
    if (route.path === resolvePath(props.to)) return true;
    return props.activeOn.includes(route.name);
  }
  return false;
});

const activeChild = computed(() => {
  const pathSame = navigableChildren.value.find(
    child => child.to && route.path === resolvePath(child.to)
  );
  if (pathSame) return pathSame;

  const activeOnPages = navigableChildren.value.filter(child =>
    child.activeOn?.includes(route.name)
  );

  if (activeOnPages.length > 0) {
    const rankedPage = activeOnPages.find(child => {
      return Object.keys(child.to.params)
        .map(key => {
          return String(child.to.params[key]) === String(route.params[key]);
        })
        .every(match => match);
    });
    return rankedPage ?? activeOnPages[0];
  }

  return navigableChildren.value.find(
    child => child.to && route.path.startsWith(resolvePath(child.to))
  );
});

const hasActiveChild = computed(() => {
  return activeChild.value !== undefined;
});

const toggleTrigger = () => {
  // Grupo com destino próprio (ex.: Campanhas → lista) navega pra ELE;
  // só sem `to` cai no primeiro filho acessível.
  if (props.to) {
    router.push(props.to);
  } else if (
    hasAccessibleChildren.value &&
    !isExpanded.value &&
    !hasActiveChild.value
  ) {
    const firstItem = accessibleItems.value[0];
    router.push(firstItem.to);
  }
  setExpandedItem(props.name);
};

onMounted(async () => {
  await nextTick();
  if (hasActiveChild.value) {
    setExpandedItem(props.name);
  }
});

// Popover state
const isPopoverOpen = computed(() => props.activeMenuName === props.name);

const handleClick = () => {
  if (hasChildren.value) {
    if (isPopoverOpen.value) {
      emit('closeSubmenu');
    } else {
      emit('openSubmenu', {
        name: props.name,
        label: props.label,
        icon: props.icon,
        children: props.children,
      });
    }
  } else if (props.to) {
    router.push(props.to);
  }
};

const handleItemClick = () => {
  emit('closeSubmenu');
};

// Organiza os children para o popover
const organizedChildren = computed(() => {
  if (!props.children) return [];
  return props.children
    .map(child => {
      if (child.children) {
        const allowedSubChildren = child.children.filter(subChild =>
          isAllowed(subChild.to)
        );
        if (allowedSubChildren.length === 0) return null;
        return {
          type: 'subgroup',
          label: child.label,
          icon: child.icon,
          children: allowedSubChildren,
        };
      }
      if (!isAllowed(child.to)) return null;
      return { type: 'item', ...child };
    })
    .filter(Boolean);
});

const isChildActive = item => {
  if (!item.to) return false;
  const itemPath = resolvePath(item.to);
  if (route.path === itemPath) return true;
  if (item.activeOn?.includes(route.name)) return true;
  // Only use startsWith if no activeOn is defined to avoid false positives
  // e.g. /pipeline/calendar should not match /pipeline
  if (!item.activeOn || item.activeOn.length === 0) {
    return route.path.startsWith(itemPath + '/') || route.path === itemPath;
  }
  return false;
};

// Refs para click-outside
const popoverRef = ref(null);
const triggerRef = ref(null);

// Calcula a posição do popover para não ficar cortado
const popoverStyle = ref({});

const updatePopoverPosition = async () => {
  if (!triggerRef.value || !isPopoverOpen.value) return;

  await nextTick();

  const triggerRect = triggerRef.value.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const spaceBelow = viewportHeight - triggerRect.bottom;

  // Se está perto do fundo da tela, ancora no fundo da viewport
  if (spaceBelow < 300) {
    popoverStyle.value = {
      top: 'auto',
      bottom: '20px',
      maxHeight: `${viewportHeight - 40}px`,
      overflowY: 'auto',
    };
  } else {
    // Posiciona normalmente a partir do topo do trigger
    popoverStyle.value = {
      top: `${triggerRect.top}px`,
      bottom: 'auto',
      maxHeight: `${viewportHeight - triggerRect.top - 20}px`,
      overflowY: 'auto',
    };
  }
};

// Atualiza posição quando o popover abre
watch(isPopoverOpen, async newVal => {
  if (newVal) {
    await nextTick();
    updatePopoverPosition();
  }
});

// Fechar popover quando clicar fora (exceto no trigger)
const handleClickOutside = event => {
  // Ignora se clicou no popover ou no trigger
  const isInsidePopover =
    popoverRef.value && popoverRef.value.contains(event.target);
  const isInsideTrigger =
    triggerRef.value && triggerRef.value.contains(event.target);

  if (!isInsidePopover && !isInsideTrigger) {
    emit('closeSubmenu');
  }
};

watch(isPopoverOpen, newVal => {
  if (newVal) {
    // Adiciona listener depois de um pequeno delay para evitar que o click que abriu também feche
    setTimeout(() => {
      document.addEventListener('click', handleClickOutside, true);
    }, 50);
  } else {
    document.removeEventListener('click', handleClickOutside, true);
  }
});

// Fechar com Escape
const handleEscape = event => {
  if (event.key === 'Escape' && isPopoverOpen.value) {
    emit('closeSubmenu');
  }
};

// Limpa o event listener quando o componente é destruído
onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside, true);
  document.removeEventListener('keydown', handleEscape);
});

// Adiciona/remove listener do Escape baseado no estado do popover
watch(
  isPopoverOpen,
  newVal => {
    if (newVal) {
      document.addEventListener('keydown', handleEscape);
    } else {
      document.removeEventListener('keydown', handleEscape);
    }
  },
  { immediate: true }
);
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Policy
    v-if="!hasChildren || hasAccessibleChildren"
    :permissions="resolvePermissions(to)"
    :feature-flag="resolveFeatureFlag(to)"
    as="li"
    class="grid gap-1 text-sm cursor-pointer select-none relative"
    :data-tour="`sidebar-${name.toLowerCase().replace(/\s+/g, '-')}`"
  >
    <!-- Modo Colapsado com Popover -->
    <template v-if="isCollapsed">
      <div class="relative">
        <div ref="triggerRef">
          <SidebarGroupHeader
            :icon="icon"
            :name="name"
            :label="label"
            :to="hasChildren ? null : to"
            :getter-keys="getterKeys"
            :is-active="isActive || isPopoverOpen"
            :has-active-child="hasActiveChild"
            :expandable="false"
            :is-expanded="false"
            :on-click="handleClick"
            is-collapsed
          />
        </div>

        <!-- Popover Flutuante -->
        <div
          v-if="isPopoverOpen && hasChildren"
          ref="popoverRef"
          class="fixed z-[100] min-w-[220px] max-w-[280px] bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak rounded-xl shadow-lg pt-2 pb-3 px-2"
          :class="isCollapsed ? 'left-[68px]' : 'left-[228px]'"
          :style="popoverStyle"
        >
          <!-- Header -->
          <div
            class="px-2 py-1.5 text-xs font-semibold text-n-slate-11 uppercase tracking-wide border-b border-n-weak mb-1 flex items-center gap-2"
          >
            <Icon v-if="icon" :icon="icon" class="size-3" />
            <span>{{ label }}</span>
          </div>

          <!-- Items -->
          <ul class="list-none m-0 p-0 grid gap-0.5">
            <template
              v-for="child in organizedChildren"
              :key="child.name || child.label"
            >
              <!-- Item direto -->
              <li v-if="child.type === 'item'">
                <router-link
                  :to="child.to"
                  :title="child.label"
                  class="flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                  :class="{
                    'bg-n-blue-3 text-n-blue-text font-semibold':
                      isChildActive(child),
                  }"
                  @click="handleItemClick"
                >
                  <Icon
                    v-if="child.icon"
                    :icon="child.icon"
                    class="size-4 text-n-slate-11"
                  />
                  <span class="truncate">{{ child.label }}</span>
                </router-link>
              </li>

              <!-- Subgrupo -->
              <li v-else-if="child.type === 'subgroup'" class="mt-2">
                <div
                  class="flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-n-slate-10 uppercase tracking-wide"
                >
                  <Icon
                    v-if="child.icon"
                    :icon="child.icon"
                    class="size-3 text-n-slate-9"
                  />
                  <span>{{ child.label }}</span>
                </div>
                <ul class="list-none m-0 p-0 grid gap-0.5 mt-1">
                  <li v-for="subChild in child.children" :key="subChild.name">
                    <router-link
                      :to="subChild.to"
                      :title="subChild.label"
                      class="flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
                      :class="{
                        'bg-n-blue-3 text-n-blue-text font-semibold':
                          isChildActive(subChild),
                      }"
                      @click="handleItemClick"
                    >
                      <component
                        :is="subChild.icon"
                        v-if="typeof subChild.icon === 'object'"
                        class="size-3 flex-shrink-0"
                      />
                      <Icon
                        v-else-if="subChild.icon"
                        :icon="subChild.icon"
                        class="size-3 text-n-slate-11 flex-shrink-0"
                      />
                      <span class="truncate">{{ subChild.label }}</span>
                    </router-link>
                  </li>
                </ul>
              </li>
            </template>
          </ul>
        </div>
      </div>
    </template>

    <!-- Modo Expandido (não usado, mas mantido) -->
    <template v-else>
      <SidebarGroupHeader
        :icon="icon"
        :name="name"
        :label="label"
        :to="to"
        :getter-keys="getterKeys"
        :is-active="isActive"
        :has-active-child="hasActiveChild"
        :expandable="hasChildren"
        :is-expanded="isExpanded"
        :on-click="onClick"
        :is-collapsed="false"
        @toggle="toggleTrigger"
      />
      <ul
        v-if="hasChildren"
        v-show="isExpanded || hasActiveChild"
        class="grid m-0 list-none sidebar-group-children"
      >
        <template v-for="child in children" :key="child.name">
          <SidebarSubGroup
            v-if="child.children"
            :label="child.label"
            :icon="child.icon"
            :children="child.children"
            :is-expanded="isExpanded"
            :active-child="activeChild"
          />
          <SidebarGroupLeaf
            v-else-if="isAllowed(child.to)"
            v-show="isExpanded || activeChild?.name === child.name"
            v-bind="child"
            :active="activeChild?.name === child.name"
          />
        </template>
      </ul>
      <ul v-else-if="isExpandable && isExpanded">
        <SidebarGroupEmptyLeaf />
      </ul>
    </template>
  </Policy>
</template>

<style>
.sidebar-group-children .child-item::before {
  content: '';
  position: absolute;
  width: 0.125rem;
  height: 100%;
}

.sidebar-group-children .child-item:first-child::before {
  border-radius: 4px 4px 0 0;
}

.sidebar-group-children > .child-item:last-child::before,
.sidebar-group-children
  > *:last-child
  > *:last-child
  > .child-item:last-child::before {
  height: 20%;
}

.sidebar-group-children > .child-item:last-child::after,
.sidebar-group-children
  > *:last-child
  > *:last-child
  > .child-item:last-child::after {
  content: '';
  position: absolute;
  width: 10px;
  height: 12px;
  bottom: calc(50% - 2px);
  border-bottom-width: 0.125rem;
  border-left-width: 0.125rem;
  border-right-width: 0px;
  border-top-width: 0px;
  border-radius: 0 0 0 4px;
  left: 0;
}

#app[dir='rtl'] .sidebar-group-children > .child-item:last-child::after,
#app[dir='rtl']
  .sidebar-group-children
  > *:last-child
  > *:last-child
  > .child-item:last-child::after {
  right: 0;
  border-bottom-width: 0.125rem;
  border-right-width: 0.125rem;
  border-left-width: 0px;
  border-top-width: 0px;
  border-radius: 0 0 4px 0px;
}
</style>
